import { formatKstDate } from "@sobidogam/domain";
import type {
	Channel,
	CommerceEntitlement,
	CommercePort,
} from "@sobidogam/platform-ports";
import { useState } from "react";
import { PRICING } from "./public/guides";

const SKU_NAMES: Record<CommerceEntitlement["sku"], string> = {
	lifetime: "기억 보관함 평생권",
	annual_report: "연간 기억책",
};

/**
 * 소비도감 플러스: what stays free, what is a one-time purchase, how refund
 * and restore work, and the user's purchase list. Payment stays off until
 * the owner activates it; this screen never shows a fake purchase.
 */
export function PlusScreen({
	channel,
	commerce,
	entitlements,
	onEntitlements,
}: {
	channel: Channel;
	commerce?: CommercePort | undefined;
	entitlements: CommerceEntitlement[];
	onEntitlements: (entitlements: CommerceEntitlement[]) => void;
}) {
	const [busy, setBusy] = useState(false);
	const [notice, setNotice] = useState("");
	const purchasable =
		commerce?.available === true &&
		commerce.initialFreeCohortComplete &&
		commerce.commercialReleaseApproved;
	const owned = entitlements.filter((item) => !item.revokedAt);
	return (
		<main className="app-main plus-screen">
			<p className="eyebrow">소비도감 플러스</p>
			<h1>기록은 평생 무료, 소장만 한 번 구매</h1>
			<p className="page-lede">
				매일 쓰는 기능에는 결제 문을 두지 않아요. 광고도 넣지 않아요.
			</p>
			<section>
				<h2>무료로 계속 쓰는 것</h2>
				<ul className="guide-list">
					{PRICING.free.map((item) => (
						<li key={item}>{item}</li>
					))}
				</ul>
			</section>
			<section>
				<h2>한 번 구매하는 것</h2>
				{PRICING.paid.map((offer) => (
					<article className="pricing-offer" key={offer.name}>
						<header>
							<strong>{offer.name}</strong>
							<span className="pricing-amount">{offer.price}</span>
						</header>
						<small>{offer.kind} · 구독 아님</small>
						<p>{offer.body}</p>
					</article>
				))}
				{purchasable ? (
					<p className="notice">
						구매는 리포트의 '기억책 옵션 살펴보기'에서 할 수 있어요.
					</p>
				) : (
					<p className="notice">
						{channel === "ait"
							? "토스 앱에서 곧 구매할 수 있게 준비 중이에요. 지금은 구매할 수 없고, 결제도 되지 않아요."
							: "웹에서는 결제를 받지 않아요. 토스 앱의 소비도감에서 구매할 수 있게 준비 중이에요."}{" "}
						기본 리포트, 올해 결산 미리 보기, CSV·JSON 내보내기는 계속 무료예요.
					</p>
				)}
			</section>
			<section>
				<h2>내 구매</h2>
				{owned.length ? (
					<ul className="purchase-list">
						{owned.map((item) => (
							<li key={`${item.sku}-${item.periodKey ?? "all"}`}>
								<strong>
									{SKU_NAMES[item.sku]}
									{item.periodKey ? ` (${item.periodKey})` : ""}
								</strong>
								<small>{formatKstDate(item.grantedAt)} 구매</small>
							</li>
						))}
					</ul>
				) : (
					<p className="field-hint">아직 구매한 상품이 없어요.</p>
				)}
				<button
					className="quiet-button full-width"
					type="button"
					disabled={busy}
					onClick={async () => {
						if (!commerce?.available) {
							setNotice("구매 기록이 없어요. 결제가 열린 뒤 복원할 수 있어요.");
							return;
						}
						setBusy(true);
						try {
							const restored = await commerce.restore();
							onEntitlements(restored);
							setNotice(
								restored.some((item) => !item.revokedAt)
									? "구매 내역을 복원했어요."
									: "복원할 구매 내역이 없어요.",
							);
						} catch {
							setNotice(
								"구매 내역을 확인하지 못했어요. 잠시 후 다시 시도해 주세요.",
							);
						} finally {
							setBusy(false);
						}
					}}
				>
					이전에 구매했다면 복원
				</button>
				{notice ? <output className="form-note">{notice}</output> : null}
			</section>
			<section>
				<h2>해지·환불</h2>
				<ul className="guide-list">
					{PRICING.rules.map((rule) => (
						<li key={rule}>{rule}</li>
					))}
				</ul>
			</section>
		</main>
	);
}
